import React, { useState, useEffect, useCallback } from "react";

const TYPE_CONFIG = {
  success: {
    gradient: "from-emerald-500 to-teal-500",
    iconBg: "bg-emerald-50",
    iconRing: "ring-emerald-100",
    iconColor: "text-emerald-600",
    label: "Success",
    iconPath: "M5 13l4 4L19 7",
  },
  error: {
    gradient: "from-red-500 to-rose-500",
    iconBg: "bg-red-50",
    iconRing: "ring-red-100",
    iconColor: "text-red-600",
    label: "Error",
    iconPath:
      "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  },
  warning: {
    gradient: "from-amber-400 to-orange-400",
    iconBg: "bg-amber-50",
    iconRing: "ring-amber-100",
    iconColor: "text-amber-600",
    label: "Warning",
    iconPath:
      "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  },
  info: {
    gradient: "from-blue-500 to-indigo-500",
    iconBg: "bg-blue-50",
    iconRing: "ring-blue-100",
    iconColor: "text-blue-600",
    label: "Info",
    iconPath:
      "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  },
};

const DEFAULT_DURATION = 2500;

const Toast = ({ toast, onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const duration = toast.duration ?? DEFAULT_DURATION;
  const cfg = TYPE_CONFIG[toast.type] || TYPE_CONFIG.info;

  const dismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => onDismiss(toast.id), 250);
  }, [onDismiss, toast.id]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const t = setTimeout(dismiss, duration);
    return () => clearTimeout(t);
  }, [duration, dismiss]);

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center z-[80] p-4 transition-all duration-200 ${
        visible ? "bg-black/30" : "bg-transparent"
      }`}
      onClick={dismiss}
    >
      <div
        className={`relative bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden transition-all duration-200 ${
          visible ? "scale-100 opacity-100" : "scale-90 opacity-0"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="alert"
      >
        {/* Top gradient bar */}
        <div className={`h-1.5 bg-gradient-to-r ${cfg.gradient}`} />

        {/* Close button */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="px-6 pt-6 pb-5 flex flex-col items-center text-center">
          {/* Icon circle */}
          <div
            className={`w-16 h-16 rounded-full ${cfg.iconBg} ring-4 ${cfg.iconRing} flex items-center justify-center mb-4`}
          >
            <svg
              className={`w-8 h-8 ${cfg.iconColor}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={cfg.iconPath}
              />
            </svg>
          </div>

          {/* Label */}
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1.5">
            {cfg.label}
          </p>

          {/* Message */}
          <p className="text-sm font-medium text-gray-800 leading-relaxed">
            {toast.message}
          </p>

          {/* Auto-dismiss progress bar. `direction: rtl` flips the shrink
              animation so the left edge moves rightward as time elapses
              (drain left-to-right) instead of the right edge moving leftward. */}
          <div className="mt-5 w-full h-1 bg-gray-100 rounded-full overflow-hidden" style={{ direction: "rtl" }}>
            <div
              className={`h-full bg-gradient-to-r ${cfg.gradient} rounded-full`}
              style={{ animation: `shrink ${duration}ms linear forwards` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Toast;
export { TYPE_CONFIG as TYPE_STYLES, DEFAULT_DURATION };
